import { ApplicationConfig } from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";
import { definePreset } from "@primeng/themes";

export const MyPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: "0",
      xs: "2px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
    },
    primary: {
      50: "#fbeee3",
      100: "#f6d6b8",
      200: "#f0bd8c",
      300: "#eaa561",
      400: "#e48c35",
      500: "#D88239",
      600: "#bf7232",
      700: "#a6632c",
      800: "#8c5325",
      900: "#73441f",
      950: "#593518",
    },
    // 🔹 Custom navy-based surface palette for dark mode
    surface: {
      0: "#ffffff",
      50: "#f5f7fa",
      100: "#e4e9f0",
      200: "#c8d1e0",
      300: "#a9b7ce",
      400: "#7d92b2",
      500: "#5d7496",
      600: "#495c7a",
      700: "#3a4a66",
      800: "#2c4668", // main dark blue
      900: "#243652",
      950: "#1a263d",
    },
  },
  semantic: {
    transitionDuration: "0.2s",
    focusRing: {
      width: "1px",
      style: "solid",
      color: "{primary.color}",
      offset: "2px",
      shadow: "none",
    },
    disabledOpacity: "0.6",
    iconSize: "1rem",
    anchorGutter: "2px",
    primary: {
      50: "#fbeee3",
      100: "#f6d6b8",
      200: "#f0bd8c",
      300: "#eaa561",
      400: "#e48c35",
      500: "#D88239",
      600: "#bf7232",
      700: "#a6632c",
      800: "#8c5325",
      900: "#73441f",
      950: "#593518",
    },
    formField: {
      paddingX: "0.75rem",
      paddingY: "0.5rem",
      sm: { fontSize: "0.875rem", paddingX: "0.625rem", paddingY: "0.375rem" },
      lg: { fontSize: "1.125rem", paddingX: "0.875rem", paddingY: "0.625rem" },
      borderRadius: "{border.radius.md}",
      focusRing: { width: "0", style: "none", color: "transparent", offset: "0", shadow: "none" },
      transitionDuration: "{transition.duration}",
    },
    list: {
      padding: "0.25rem 0.25rem",
      gap: "2px",
      header: { padding: "0.5rem 1rem 0.25rem 1rem" },
      option: { padding: "0.5rem 0.75rem", borderRadius: "{border.radius.sm}" },
      optionGroup: { padding: "0.5rem 0.75rem", fontWeight: "600" },
    },
    content: { borderRadius: "{border.radius.md}" },
    mask: { transitionDuration: "0.15s" },
    navigation: {
      list: { padding: "0.25rem 0.25rem", gap: "2px" },
      item: { padding: "0.5rem 0.75rem", borderRadius: "{border.radius.sm}", gap: "0.5rem" },
      submenuLabel: { padding: "0.5rem 0.75rem", fontWeight: "600" },
      submenuIcon: { size: "0.875rem" },
    },
    overlay: {
      select: {
        borderRadius: "{border.radius.md}",
        shadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
      },
      popover: {
        borderRadius: "{border.radius.md}",
        padding: "0.75rem",
        shadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
      },
      modal: {
        padding: "1.25rem",
        shadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
      },
      navigation: {
        shadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
      },
    },
    colorScheme: {
      light: {
        primary: {
          color: "{primary.500}",
          contrastColor: "#ffffff",
          hoverColor: "{primary.600}",
          activeColor: "{primary.700}",
        },
        highlight: {
          background: "{primary.50}",
          focusBackground: "{primary.100}",
          color: "{primary.700}",
          focusColor: "{primary.800}",
        },
      },
      dark: {
        primary: {
          color: "{primary.400}",
          contrastColor: "{surface.50}", // light text on dark blue
          hoverColor: "{primary.300}",
          activeColor: "{primary.200}",
        },
        highlight: {
          background: "color-mix(in srgb, {primary.400}, transparent 84%)",
          focusBackground: "color-mix(in srgb, {primary.400}, transparent 76%)",
          color: "rgba(255,255,255,.87)",
          focusColor: "rgba(255,255,255,.87)",
        },
        surface: {
          background: "{surface.800}", // main dark blue background
          card: "{surface.700}",       // slightly lighter for cards/panels
          overlay: "{surface.900}",    // for modals/popovers
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
      },
    }),
  ],
};
